import { Button, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { hrApi, hrNoticesApi } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

export function NoticesTab() {
  const [filterKind, setFilterKind] = useState<"all" | "notice" | "letter">("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedNoticeId, setExpandedNoticeId] = useState<string | null>(null);

  // Form state
  const [kind, setKind] = useState<"notice" | "letter">("notice");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [urgency, setUrgency] = useState<"normal" | "important" | "urgent">("normal");
  const [targetType, setTargetType] = useState<"all" | "position" | "person" | "station">("all");
  const [targetId, setTargetId] = useState("");
  const [requiresAck, setRequiresAck] = useState(false);

  const noticesQuery = useQuery({
    queryKey: ["hr", "notices", filterKind],
    queryFn: () => hrNoticesApi.listNotices(filterKind === "all" ? undefined : { kind: filterKind }),
  });

  const positionsQuery = useQuery({
    queryKey: ["hr", "positions"],
    queryFn: () => hrApi.listPositions(),
  });

  const peopleQuery = useQuery({
    queryKey: ["hr", "people"],
    queryFn: () => hrApi.listPeople(),
  });

  const createNoticeAction = useAction(async () => {
    let targetName = "";
    if (targetType === "all") {
      targetName = "همه پرسنل";
    } else if (targetType === "position" && targetId) {
      const pos = positionsQuery.data?.find((p) => p.id === targetId);
      targetName = pos ? pos.name : "";
    } else if (targetType === "person" && targetId) {
      const per = peopleQuery.data?.find((p) => p.personId === targetId || p.id === targetId);
      targetName = per ? per.personName : "";
    }

    await hrNoticesApi.createNotice({
      kind,
      title: title.trim(),
      content: content.trim(),
      urgency,
      targetType,
      targetId: targetId || null,
      targetName,
      requiresAck,
    });

    setShowAddModal(false);
    setTitle("");
    setContent("");
    setUrgency("normal");
    setTargetType("all");
    setTargetId("");
    setRequiresAck(false);
  }, [["hr", "notices"]]);

  const ackAction = useAction(
    async (id: string) => {
      await hrNoticesApi.ackNotice(id);
    },
    [["hr", "notices"]],
  );

  const notices = noticesQuery.data ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <Panel
        title="پیام‌ها و ابلاغیه‌های پرسنلی"
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setShowAddModal(true);
            }}
            id="btn-add-notice"
          >
            ارسال پیام یا نامه جدید
          </Button>
        }
      >
        <p className="muted" style={{ margin: "0 0 var(--s3) 0" }}>
          ارسال اطلاعیه‌ها، پیام‌های فوری با اعلان صوتی، نامه‌های اداری و دریافت تاییدیه «دیدم» از پرسنل
        </p>

        <div
          className="row wrap"
          style={{ gap: "var(--s2)", marginBottom: "var(--s4)", alignItems: "center" }}
        >
          <span className="muted" style={{ fontSize: "0.875rem" }}>
            فیلتر نوع:
          </span>
          <button
            type="button"
            className={`chip ${filterKind === "all" ? "primary" : ""}`}
            onClick={() => {
              setFilterKind("all");
            }}
          >
            همه
          </button>
          <button
            type="button"
            className={`chip ${filterKind === "notice" ? "primary" : ""}`}
            onClick={() => {
              setFilterKind("notice");
            }}
          >
            پیام‌ها
          </button>
          <button
            type="button"
            className={`chip ${filterKind === "letter" ? "primary" : ""}`}
            onClick={() => {
              setFilterKind("letter");
            }}
          >
            نامه‌های رسمی
          </button>
        </div>

        {noticesQuery.isLoading && <p className="muted">در حال بارگذاری پیام‌ها...</p>}

        {!noticesQuery.isLoading && notices.length === 0 && (
          <p className="muted" style={{ textAlign: "center", padding: "var(--s4)" }}>
            پیامی برای نمایش وجود ندارد.
          </p>
        )}

        <div className="stack" style={{ gap: "var(--s3)" }}>
          {notices.map((n) => {
            const isUrgent = n.urgency === "urgent";
            const isImportant = n.urgency === "important";
            const isExpanded = expandedNoticeId === n.id;

            return (
              <div
                key={n.id}
                className="card"
                style={{
                  border: isUrgent
                    ? "2px solid var(--danger, #ef4444)"
                    : isImportant
                      ? "2px solid var(--warning, #f59e0b)"
                      : "1px solid var(--border)",
                  borderRadius: "var(--r-md)",
                  padding: "var(--s3)",
                  background: isUrgent
                    ? "rgba(239, 68, 68, 0.04)"
                    : isImportant
                      ? "rgba(245, 158, 11, 0.04)"
                      : undefined,
                }}
              >
                <div
                  className="row wrap"
                  style={{ justifyContent: "space-between", alignItems: "flex-start", gap: "var(--s2)" }}
                >
                  <div className="row wrap" style={{ gap: "var(--s2)", alignItems: "center" }}>
                    <span className="chip">{n.kind === "letter" ? "نامه رسمی" : "پیام"}</span>
                    {n.urgency === "urgent" && <span className="chip red">فوری 🔔</span>}
                    {n.urgency === "important" && <span className="chip amber">مهم</span>}
                    {n.urgency === "normal" && <span className="chip muted">عادی</span>}
                    <span className="muted" style={{ fontSize: "0.85rem" }}>
                      گیرنده: <strong>{n.targetName || "همه"}</strong>
                    </span>
                    <span className="muted" style={{ fontSize: "0.85rem" }}>
                      فرستنده: {n.senderPersonName}
                    </span>
                  </div>

                  <span className="muted" style={{ fontSize: "0.8rem" }}>
                    {n.createdAt.slice(0, 16).replace("T", " ")}
                  </span>
                </div>

                {n.title && (
                  <h4 style={{ margin: "var(--s2) 0 var(--s1) 0", fontSize: "1.05rem" }}>{n.title}</h4>
                )}

                <p style={{ margin: "var(--s2) 0", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{n.content}</p>

                <div
                  className="row wrap"
                  style={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "var(--s2)",
                    paddingTop: "var(--s2)",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  <div className="row wrap" style={{ gap: "var(--s2)", alignItems: "center" }}>
                    {n.requiresAck ? (
                      <>
                        <span className="muted" style={{ fontSize: "0.85rem" }}>
                          تأییدیه: {n.ackedCount} از {n.totalRecipients} نفر
                        </span>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setExpandedNoticeId(isExpanded ? null : n.id);
                          }}
                        >
                          {isExpanded ? "بستن جزئیات آمار" : "مشاهده آمار تأیید"}
                        </Button>
                      </>
                    ) : (
                      <span className="muted" style={{ fontSize: "0.85rem" }}>
                        بدون نیاز به تأیید
                      </span>
                    )}
                  </div>

                  {n.requiresAck && (
                    <div>
                      {n.hasAcked ? (
                        <span className="chip green" id={`ack-done-${n.id}`}>
                          ✓ تأیید شد (دیدم)
                        </span>
                      ) : (
                        <Button
                          variant="primary"
                          id={`btn-ack-${n.id}`}
                          className="btn-ack-notice"
                          onClick={() => {
                            ackAction.run(n.id);
                          }}
                          disabled={ackAction.busy}
                        >
                          دیدم
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {isExpanded && n.requiresAck && (
                  <div
                    style={{
                      marginTop: "var(--s2)",
                      padding: "var(--s2)",
                      background: "var(--bg-subtle, #f9fafb)",
                      borderRadius: "var(--r-sm)",
                      fontSize: "0.85rem",
                    }}
                  >
                    {n.acks.length > 0 && (
                      <div style={{ marginBottom: "var(--s2)" }}>
                        <strong>تأییدکنندگان ({n.acks.length}):</strong>{" "}
                        {n.acks.map((a) => a.personName).join("، ")}
                      </div>
                    )}
                    {n.unackedStaff.length > 0 && (
                      <div>
                        <strong className="text-danger">در انتظار تأیید ({n.unackedStaff.length}):</strong>{" "}
                        {n.unackedStaff.map((u) => u.personName).join("، ")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      {/* Add Notice / Letter Modal */}
      {showAddModal && (
        <div
          className="modal-backdrop"
          onClick={() => {
            setShowAddModal(false);
          }}
        >
          <div
            className="modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ maxWidth: 560 }}
          >
            <h3>ارسال پیام یا نامه جدید</h3>

            <div className="stack" style={{ gap: "var(--s3)", marginTop: "var(--s3)" }}>
              <div className="field">
                <label htmlFor="notice-kind-select">نوع ارسال</label>
                <select
                  id="notice-kind-select"
                  className="input"
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value as "notice" | "letter");
                  }}
                >
                  <option value="notice">پیام پرسنلی</option>
                  <option value="letter">نامه اداری و رسمی</option>
                </select>
              </div>

              {kind === "letter" && (
                <Field
                  id="notice-title-input"
                  label="عنوان نامه"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                  }}
                  placeholder="مثال: حکم انتصاب سرپرست بخش پخت"
                />
              )}

              <div className="field">
                <label htmlFor="notice-content-input">متن پیام / نامه</label>
                <textarea
                  id="notice-content-input"
                  className="input"
                  rows={4}
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value);
                  }}
                  placeholder="متن پیام را بنویسید..."
                />
              </div>

              <div className="row wrap" style={{ gap: "var(--s3)" }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="notice-urgency-select">فوریت / سطح صدا</label>
                  <select
                    id="notice-urgency-select"
                    className="input"
                    value={urgency}
                    onChange={(e) => {
                      setUrgency(e.target.value as "normal" | "important" | "urgent");
                    }}
                  >
                    <option value="normal">عادی (بی‌صدا)</option>
                    <option value="important">مهم (صدای توجه)</option>
                    <option value="urgent">فوری (صدای اضطراری)</option>
                  </select>
                </div>

                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="notice-target-type-select">گیرندگان</label>
                  <select
                    id="notice-target-type-select"
                    className="input"
                    value={targetType}
                    onChange={(e) => {
                      setTargetType(e.target.value as "all" | "position" | "person" | "station");
                      setTargetId("");
                    }}
                  >
                    <option value="all">همه پرسنل</option>
                    <option value="position">سمت شغلی مشخص</option>
                    <option value="person">شخص خاص</option>
                    <option value="station">ایستگاه کاری</option>
                  </select>
                </div>
              </div>

              {targetType === "position" && (
                <div className="field">
                  <label htmlFor="notice-position-select">انتخاب سمت سازمانی</label>
                  <select
                    id="notice-position-select"
                    className="input"
                    value={targetId}
                    onChange={(e) => {
                      setTargetId(e.target.value);
                    }}
                  >
                    <option value="">-- انتخاب سمت --</option>
                    {positionsQuery.data?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {targetType === "person" && (
                <div className="field">
                  <label htmlFor="notice-person-select">انتخاب پرسنل</label>
                  <select
                    id="notice-person-select"
                    className="input"
                    value={targetId}
                    onChange={(e) => {
                      setTargetId(e.target.value);
                    }}
                  >
                    <option value="">-- انتخاب پرسنل --</option>
                    {peopleQuery.data?.map((p) => (
                      <option key={p.personId} value={p.personId}>
                        {p.personName} ({p.positionName || "بدون سمت"})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {targetType === "station" && (
                <div className="field">
                  <label htmlFor="notice-station-select">ایستگاه کاری</label>
                  <select
                    id="notice-station-select"
                    className="input"
                    value={targetId}
                    onChange={(e) => {
                      setTargetId(e.target.value);
                    }}
                  >
                    <option value="">-- انتخاب ایستگاه --</option>
                    <option value="prep">آماده‌سازی</option>
                    <option value="hot_kitchen">آشپزخانه گرم</option>
                    <option value="packing">بسته‌بندی و توزیع</option>
                    <option value="dishwashing">ظرفشویی</option>
                  </select>
                </div>
              )}

              <label className="row" style={{ gap: "var(--s2)", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={requiresAck}
                  onChange={(e) => {
                    setRequiresAck(e.target.checked);
                  }}
                  id="notice-requires-ack-check"
                />
                <span>نیاز به ثبت تاییدیه «دیدم» توسط دریافت‌کنندگان</span>
              </label>

              <ErrorLine error={createNoticeAction.error} />

              <div
                className="row"
                style={{ gap: "var(--s2)", justifyContent: "flex-end", marginTop: "var(--s3)" }}
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowAddModal(false);
                  }}
                >
                  انصراف
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    createNoticeAction.run(undefined);
                  }}
                  disabled={createNoticeAction.busy || !content.trim()}
                  id="btn-submit-notice"
                >
                  {createNoticeAction.busy ? "در حال ارسال..." : "ارسال پیام"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
